import { useCallback, useMemo, useState } from 'react';
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Camera,
  Mail,
  Siren,
  SquareArrowOutUpLeft,
  Webhook,
  Server,
  Cloud,
  Database,
} from 'lucide-react';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { buttonVariants } from '@/components/ui/button';
import { Button } from '@/components/ui/button';
import { ColumnDef } from '@tanstack/react-table';
import {
  DataTable,
  DataTableContainer,
  DataTableContent,
  DataTableFilterBar,
  DataTableHeader,
  DataTableRows,
  DataTableWrapper,
  DataTableFooter,
  DataTableUtilityBar,
} from '@/components/data-table';
import {
  FilterField,
  FilterForm,
  FilterHeader,
  FilterInlineResetButton,
} from '@/components/filters/FilterForm';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SearchDevicesQuery } from '@/lib/api/devices/devices';
import { DeviceDto as ApiDeviceDto } from '@/lib/api/types.gen';
import { Comparator, FilterOperator, FilterGroup, SortOrder } from '@/lib/api/types.gen';
import { routes } from '@/lib/routing/route-paths';

interface DeviceTableData {
  id: string;
  name: string;
  integrationType: string;
  brand: string;
  model: string;
  customerName: string;
  customerId: string;
  externalId: string;
  sources: {
    smtp: boolean;
    webhook: boolean;
    lifecycle: boolean;
  };
  nvr: NonNullable<ApiDeviceDto['nvr']>;
  integration: NonNullable<ApiDeviceDto['integration']>;
  customer: NonNullable<ApiDeviceDto['customer']>;
}

function extractDeviceTableData(devices: ApiDeviceDto[]): DeviceTableData[] {
  return devices
    .filter(({ nvr, customer, integration }) => nvr && customer && integration && nvr.id)
    .map(({ nvr, customer, integration }) => ({
      id: nvr!.id!,
      name: nvr!.name || 'No name specified',
      integrationType: integration!.integrationType || 'UNKNOWN',
      brand: integration!.brand || '',
      model: integration!.name || '',
      customerName: customer!.name || '',
      customerId: customer!.id || '',
      externalId: nvr!.externalId || '',
      sources: {
        smtp: !!integration!.smtpSource,
        webhook: !!integration!.webhookSource,
        lifecycle: !!integration!.watchfulLifecycleTrigger,
      },
      nvr: nvr!,
      integration: integration!,
      customer: customer!,
    }));
}

const deviceColumns: ColumnDef<DeviceTableData>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    enableHiding: false,
    enableSorting: true,
    meta: { label: 'Device Name' },
    header: 'Device Name',
    cell: ({ row }) => {
      const { name, id, integrationType } = row.original;
      
      const getIcon = () => {
        switch (String(integrationType || '').toLowerCase()) {
          case 'nvr':
            return <Server className="h-5 w-5 text-lime" />;
          case 'vms':
            return <Cloud className="h-5 w-5 text-lime" />;
          case 'external_service':
            return <Database className="h-5 w-5 text-lime" />;
          default:
            return <Camera className="h-5 w-5 text-lime" />;
        }
      };

      return (
        <Link
          to={`/devices/${id}/edit`}
          className="flex items-center gap-3 font-semibold text-lime hover:underline"
        >
          {getIcon()}
          {name}
        </Link>
      );
    },
  },
  {
    id: 'integrationType',
    accessorKey: 'integrationType',
    enableSorting: false,
    meta: { label: 'Device Type' },
    header: 'Device Type',
    cell: ({ row }) => (
      <span className="font-bold">{String(row.original.integrationType || 'UNKNOWN').toUpperCase()}</span>
    ),
  },
  {
    id: 'sources',
    enableSorting: false,
    meta: { label: 'Sources' },
    header: 'Sources',
    cell: ({ row }) => {
      const { sources } = row.original;
      
      return (
        <TooltipProvider delayDuration={200}>
          <div className="flex items-center gap-2">
            {sources.smtp && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Mail className="h-5 w-5 text-white" />
                </TooltipTrigger>
                <TooltipContent>
                  <span>SMTP Events</span>
                </TooltipContent>
              </Tooltip>
            )}
            {sources.webhook && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Webhook className="h-5 w-5 text-white" />
                </TooltipTrigger>
                <TooltipContent>
                  <span>Webhook Events</span>
                </TooltipContent>
              </Tooltip>
            )}
            {sources.lifecycle && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Siren className="h-5 w-5 text-lime" />
                </TooltipTrigger>
                <TooltipContent>
                  <span>Watchful Lifecycle Events</span>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
      );
    },
  },
  {
    id: 'brandModel',
    enableSorting: false,
    meta: { label: 'Brand/Model' },
    header: 'Brand/Model',
    cell: ({ row }) => {
      const { brand, model } = row.original;
      return (
        <div>
          <span className="font-medium">{brand}</span>
          <span className="mx-1 text-gray-400">•</span>
          <span>{model}</span>
        </div>
      );
    },
  },
  {
    id: 'customer',
    enableSorting: false,
    meta: { label: 'Customer' },
    header: 'Customer',
    cell: ({ row }) => {
      const { customerName, customerId } = row.original;
      return (
        <Link
          to={`/customers/${customerId}`}
          className={`${buttonVariants({ variant: 'outline', size: 'sm' })}`}
        >
          <SquareArrowOutUpLeft />
          {customerName}
        </Link>
      );
    },
  },
  {
    id: 'externalId',
    accessorKey: 'externalId',
    enableSorting: false,
    meta: { label: 'Device ID' },
    header: 'Device ID',
    cell: ({ row }) => <span>{row.original.externalId}</span>,
  },
];

// URL param parsers for devices filters
const devicesUrlParsers = {
  devicesPage: parseAsInteger.withDefault(1),
  devicesPageSize: parseAsInteger.withDefault(20),
  name: parseAsString,
  serialNumber: parseAsString,
  filters: parseAsString,
};

export function DevicesTable() {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useQueryStates(devicesUrlParsers, { history: 'replace' });

  // Derive values from URL
  const page = urlParams.devicesPage;
  const pageSize = urlParams.devicesPageSize;
  const searchName = urlParams.name ?? '';
  const searchSerialNumber = urlParams.serialNumber ?? '';
  const sortField = 'name';
  const sortDirection = SortOrder.Ascending;
  const filtersOpen = urlParams.filters === 'open';

  // Build filter groups from URL params
  const filters = useMemo(() => {
    const filterList: { field: string; value: string; action: Comparator }[] = [];
    if (searchName) {
      filterList.push({ field: 'name', value: searchName, action: Comparator.ILike });
    }
    if (searchSerialNumber) {
      filterList.push({ field: 'serialNumber', value: searchSerialNumber, action: Comparator.ILike });
    }
    if (filterList.length === 0) return [];
    return [{ operator: FilterOperator.And, filters: filterList }];
  }, [searchName, searchSerialNumber]);

  const queryInput = useMemo(() => ({
    pageNumber: page,
    pageSize: pageSize,
    sortBy: sortField,
    sortDir: sortDirection,
    filterFilterGroups: filters
  }), [page, pageSize, sortField, sortDirection, filters]);

  const { data, isLoading, error, isFetching } = useQuery(
    SearchDevicesQuery(queryInput)
  );

  const deviceData = useMemo(() => {
    if (!data?.devices) return [];
    return extractDeviceTableData(data.devices);
  }, [data?.devices]);

  const countFilters = useCallback(
    (values: Record<string, string | Date | undefined>) =>
      Object.values(values).filter((v) => v !== undefined && v !== '').length,
    []
  );

  const initialFilterCount = useMemo(
    () =>
      countFilters({
        serialNumber: searchSerialNumber || undefined,
      }),
    [searchSerialNumber, countFilters]
  );

  const [filterCount, setFilterCount] = useState(initialFilterCount);
  const filterScrollHeight = 'calc(100vh - 128px)';

  const handleFiltersChange = useCallback(
    ({
      values,
    }: {
      filterOperator: FilterOperator;
      filterFilterGroups: FilterGroup[];
      values: Record<string, string | Date | undefined>;
    }) => {
      setUrlParams({
        serialNumber: (values.serialNumber as string) || null,
        devicesPage: 1,
      });
    },
    [setUrlParams]
  );

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setUrlParams({ devicesPageSize: newPageSize, devicesPage: 1 });
  }, [setUrlParams]);

  return (
    <div className="relative">

      <DataTable
        columns={deviceColumns}
        data={deviceData}
        loading={isLoading && !data}
        error={error}
        page={page}
        pageSize={pageSize}
        totalCount={data?.totalRows}
        onPageChange={(p: number) => setUrlParams({ devicesPage: p })}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[5, 10, 20, 100]}
        stateKey="devices-table"
        emptyMessage="No devices found"
        sidebarOpen={filtersOpen}
        onSidebarOpenChange={(open) => setUrlParams({ filters: open ? 'open' : null })}
      >
        <FilterForm
          defaultValues={{
            serialNumber: searchSerialNumber || undefined,
          }}
          onFiltersChange={({
            values,
          }: {
            filterOperator: FilterOperator;
            filterFilterGroups: FilterGroup[];
            values: Record<string, string | Date | undefined>;
          }) => {
            handleFiltersChange({ filterOperator: FilterOperator.And, filterFilterGroups: [], values });
            setFilterCount(countFilters(values));
          }}
        >
          <DataTableContainer>
            <DataTableFilterBar>
              <div className="flex h-full flex-col">
                <FilterHeader onClose={() => setUrlParams({ filters: null })} />
                <ScrollArea style={{ height: filterScrollHeight }} className="flex-1 px-4">
                  <div className="space-y-4 pb-4 pt-2">
                    <FilterInlineResetButton
                      onReset={() => {
                        setUrlParams({
                          serialNumber: null,
                          devicesPage: 1,
                        });
                        setFilterCount(0);
                      }}
                    />
                  </div>
                  <div className="space-y-4 pb-4">
                    <FilterField
                      type="text"
                      name="serialNumber"
                      label="Serial Number"
                      field="serialNumber"
                      comparator={Comparator.ILike}
                      placeholder="Search serial number..."
                    />
                  </div>
                </ScrollArea>
              </div>
            </DataTableFilterBar>
            <DataTableContent>
              <div data-onboarding="devices-utility-bar">
                <DataTableUtilityBar
                  showFilterToggle
                  showColumnVisibility
                  search={{
                    value: searchName,
                    onChange: (value) => setUrlParams({ name: value || null, devicesPage: 1 }),
                    placeholder: 'Search device name...',
                  }}
                  filterCount={filterCount}
                  actions={
                    <Button
                      size="sm"
                      onClick={() => navigate(routes.deviceAdd())}
                      data-onboarding="devices-add-button"
                    >
                      Add Device
                    </Button>
                  }
                />
              </div>
              <div data-onboarding="devices-table">
                <DataTableWrapper>
                  <DataTableHeader />
                  <DataTableRows />
                </DataTableWrapper>
              </div>
              <DataTableFooter />
            </DataTableContent>
          </DataTableContainer>
        </FilterForm>
      </DataTable>
    </div>
  );
}

